/**
 * /invite/:token — set-password page for new accounts.
 *
 * Flow:
 *   1. On mount, GET /invites/:token/preview. The endpoint returns the
 *      invitee's email + role + department, or 404 / 410 when the token is
 *      invalid / consumed / expired. We render a fallback for any non-2xx.
 *   2. On success, render a form with `password` + `confirmPassword` +
 *      `phone` (matches AcceptInviteSchema on the API). Phone is pre-filled
 *      from the preview when the BE eventually returns it (Phase 2 column);
 *      Phase 1 we ask the invitee to type it.
 *   3. POST /invites/:token/accept; on success → /login with a success toast.
 *
 * Layout: AuthLayout (route wiring lands in 5.13).
 */
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { PasswordSchema, PhoneSchema } from '@aris/shared';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { ApiError, apiFetch } from '@/lib/fetcher';

interface InvitePreview {
  email: string;
  fullName: string | null;
  roleSlug: string;
  departmentId: number | null;
  expiresAt: string;
  valid: boolean;
}

const FormSchema = z
  .object({
    password: PasswordSchema,
    confirmPassword: z.string().min(1),
    phone: PhoneSchema,
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  });
type FormValues = z.infer<typeof FormSchema>;

export function InviteAcceptPage(): React.ReactElement {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState<InvitePreview | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: '', confirmPassword: '', phone: '' },
    mode: 'onSubmit',
  });

  // Preview the invite once on mount. AbortController guards against the
  // double-fire StrictMode pattern in dev.
  React.useEffect(() => {
    const ac = new AbortController();
    setPreviewLoading(true);
    apiFetch<InvitePreview>(`/invites/${encodeURIComponent(token)}/preview`, {
      signal: ac.signal,
    })
      .then((data) => {
        if (!data.valid) {
          setPreviewError('Приглашение недействительно или просрочено');
          return;
        }
        setPreview(data);
      })
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
          setPreviewError('Приглашение недействительно или просрочено');
        } else {
          setPreviewError('Не удалось проверить приглашение');
        }
      })
      .finally(() => {
        setPreviewLoading(false);
      });
    return () => ac.abort();
  }, [token]);

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await apiFetch<{ userId: string }>(`/invites/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body: { password: values.password, phone: values.phone },
      });
      toast('Аккаунт активирован, войдите', { variant: 'success' });
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 410 || err.status === 404)) {
        // Invite consumed / not-found → unmount form, show «link invalid»
        // preview state. This is the only path that swaps to setPreviewError;
        // every other ApiError keeps the form mounted so the user can retry.
        setPreviewError('Приглашение уже использовано или просрочено');
        return;
      }
      if (err instanceof ApiError && err.status === 0) {
        // Network failure (fetcher.ts:196). `safeFetch` already toasts
        // «Нет связи» before throwing — emitting a second toast here would
        // double-fire the notification on every offline submit (R6 W2.1
        // M-3 / F-1). Suppress the page-level toast and let the form stay
        // mounted so the user can retry once connectivity returns.
        return;
      }
      if (err instanceof ApiError) {
        // R6 #4: surface the BE-supplied envelope message (e.g. the 422
        // `PHONE_TAKEN` «Этот номер телефона уже используется другим
        // пользователем» from invites.service Layer-2). Fallback to the
        // generic toast only when the BE truly returned no string.
        toast(err.message || 'Не удалось активировать аккаунт', { variant: 'error' });
        return;
      }
      // Truly unexpected throw (e.g. a thrown non-Error) — generic toast.
      toast('Не удалось активировать аккаунт', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  });

  if (previewLoading) {
    return (
      <div data-testid="invite-loading" className="space-y-4">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (previewError) {
    return (
      <div data-testid="invite-error" className="space-y-4 text-center">
        <h1 className="text-xl font-semibold text-ink">Ссылка недействительна</h1>
        <p className="text-sm text-muted-foreground">{previewError}</p>
        <Link
          to="/login"
          className="inline-block text-sm text-accent underline-offset-4 hover:underline"
        >
          Назад ко входу
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="invite-page" className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-ink">Активация аккаунта</h1>
        {preview ? (
          <p className="text-sm text-muted-foreground">Установите пароль для {preview.email}</p>
        ) : null}
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            name="phone"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Телефон</FormLabel>
                <FormControl>
                  <Input type="tel" autoComplete="tel" placeholder="+71234567890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Пароль</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="confirmPassword"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Подтверждение пароля</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={submitting}
            data-testid="invite-submit"
          >
            {submitting ? 'Активация…' : 'Активировать'}
          </Button>
        </form>
      </Form>
    </div>
  );
}
